import { api } from './api';
import { 
  MappingsResponse, 
  MappingStats, 
  MappingConflict,
  MappingFilters,
  EnhancedColumnMapping,
  ColumnProfile,
  SemanticMapping,
  MappingCompleteness,
  SemanticSearchResult
} from '../types/columnMappingReview';

export const columnMappingReviewApi = {
  getAllMappings: async (filters: MappingFilters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);
    
    const response = await api.get<MappingsResponse>(
      `/column-mapping/all?${params.toString()}`
    );
    return response.data;
  },

  getStats: async () => {
    const response = await api.get<MappingStats>(
      `/column-mapping/stats`
    );
    return response.data;
  },

  getConflicts: async () => {
    const response = await api.get<{ conflicts: MappingConflict[] }>(
      `/column-mapping/conflicts`
    );
    return response.data.conflicts;
  },

  updateMapping: async (mappingId: string, metadataName: string) => {
    const response = await api.put(
      `/column-mapping/${mappingId}`,
      null,
      { params: { metadata_name: metadataName } }
    );
    return response.data;
  },

  deleteMapping: async (mappingId: string) => {
    const response = await api.delete(
      `/column-mapping/${mappingId}`
    );
    return response.data;
  },

  getMappingHistory: async (mappingId: string) => {
    const response = await api.get(
      `/column-mapping/history/${mappingId}`
    );
    return response.data.history;
  },

  // Enhanced semantic analysis endpoints
  analyzeColumnsEnhanced: async (tenantId: string, fileData: any) => {
    const response = await api.post<{ mappings: EnhancedColumnMapping[] }>(
      `/column-mapping/analyze-columns-enhanced`,
      { tenant_id: tenantId, file_data: fileData }
    );
    return response.data.mappings;
  },

  getSemanticMapping: async (tenantId: string, taskType: string, columns: any[]) => {
    const response = await api.post<SemanticMapping>(
      `/column-mapping/semantic-mapping`,
      { 
        tenant_id: tenantId, 
        task_type: taskType, 
        available_columns: columns 
      }
    );
    return response.data;
  },

  semanticSearch: async (tenantId: string, taskType: string, query: string) => {
    const response = await api.post<{ results: SemanticSearchResult[] }>(
      `/column-mapping/semantic-search`,
      { 
        tenant_id: tenantId, 
        task_type: taskType, 
        search_query: query 
      }
    );
    return response.data.results;
  },

  getColumnProfile: async (columnId: string) => {
    const response = await api.get<ColumnProfile>(
      `/column-mapping/column-profile/${columnId}`
    );
    return response.data;
  },

  validateMappingCompleteness: async (tenantId: string, taskType: string, mappings: any) => {
    const response = await api.post<MappingCompleteness>(
      `/column-mapping/validate-mapping-completeness`,
      { 
        tenant_id: tenantId, 
        task_type: taskType, 
        provided_mappings: mappings 
      }
    );
    return response.data;
  },

  getMappingsBySemanticType: async (tenantId: string, semanticType: string) => {
    const response = await api.get<{ mappings: any[] }>(
      `/column-mapping/mappings-by-semantic-type/${semanticType}`,
      { params: { tenant_id: tenantId } }
    );
    return response.data.mappings;
  },

  getMappingsForTask: async (tenantId: string, taskType: string) => {
    const response = await api.get<{ mappings: any[] }>(
      `/column-mapping/mappings-for-task/${taskType}`,
      { params: { tenant_id: tenantId } }
    );
    return response.data.mappings;
  },

  enrichMappings: async (tenantId: string, mappingIds: string[]) => {
    const response = await api.post<{ enriched_mappings: EnhancedColumnMapping[] }>(
      `/column-mapping/enrich-mappings`,
      { 
        tenant_id: tenantId, 
        mapping_ids: mappingIds 
      }
    );
    return response.data.enriched_mappings;
  }
};